import DatePicker from "@/components/DatePicker";
import Field from "@/components/Field";
import PayrollDepartmentSelect from "@/components/PayrollDepartmentSelect";
import ContractorSelect from "@/components/ContractorSelect";
import ProjectRoleSelect from "@/modules/projects/components/ProjectRoleSelect";
import ProjectStoreSelect from "@/modules/projects/components/ProjectStoreSelect";
import { ColumnDef } from "@tanstack/react-table";
import { useMemo } from "react";
import { useTranslation } from "react-i18next";
import { ReadContractorAssignee } from "../types";
import { useContractorsAssignmentsContext } from "./ContractorsAssignmentsContext";
import { ContractorsAssignmentsRemoveButton } from "./ContractorsAssignmentsRemoveButton";
import { useProjectId } from "@/modules/projects/hooks/useProjectId";
import StoreSelect from "@/components/StoreSelect";
import { DirtyAwareCell } from "@/contexts/FormTableCellDirtyContext";
import {
  ReadRoleBasedAssignments,
  ReadRoleBasedAssignmentsWithRef,
} from "@/types/roleBasedAssignments";
import { ReadDepartmentBasedAssignments } from "@/types/departmentBasedAssignments";
import { MIN_PROJECT_DATE } from "@/constants";
import { ASSIGMENT_STATUS } from "@/schemas/assignments";
import { EnumFilter } from "@/components/data-table/components/EnumFilter";
import { NameWithSuffix } from "@/components/NameWithRef";

export const useContractorsAssignmentsColumnOverrides = () => {
  const { t } = useTranslation();
  const { isRoleBasedProject } = useContractorsAssignmentsContext();
  const projectId = useProjectId();

  const columnOverrides = useMemo(() => {
    const overrides: Record<
      string,
      ColumnDef<ReadContractorAssignee, unknown>
    > = {
      start_date: {
        id: "start_date",
        cell: ({ row }) => {
          const id = row.original.id;
          const fieldPath = `entries.${id}.start_date`;
          return (
            <Field
              name={fieldPath}
              render={({ field }) => (
                <DirtyAwareCell fieldPath={fieldPath}>
                  <DatePicker
                    {...field}
                    className="expand-to-cell"
                    datePickerProps={{ startMonth: MIN_PROJECT_DATE }}
                  />
                </DirtyAwareCell>
              )}
            />
          );
        },
      },
      end_date: {
        id: "end_date",
        cell: ({ row }) => {
          const id = row.original.id;
          const fieldPath = `entries.${id}.end_date`;
          return (
            <Field
              name={fieldPath}
              render={({ field }) => (
                <DirtyAwareCell fieldPath={fieldPath}>
                  <DatePicker
                    {...field}
                    className="expand-to-cell"
                    datePickerProps={{ startMonth: MIN_PROJECT_DATE }}
                  />
                </DirtyAwareCell>
              )}
            />
          );
        },
      },
      contractor_name: {
        id: "contractor_name",
        cell: ({ row }) => {
          const id = row.original.id;
          const fieldPath = `entries.${id}.contractor_id`;
          return (
            <Field
              name={fieldPath}
              render={({ field }) => (
                <DirtyAwareCell fieldPath={fieldPath}>
                  <ContractorSelect
                    {...field}
                    className="expand-to-cell"
                    defaultItem={{
                      id: row.original.contractor_id,
                      name: row.original.contractor_name,
                    }}
                  />
                </DirtyAwareCell>
              )}
            />
          );
        },
      },
      store_name: {
        id: "store_name",
        cell: ({ row }) => {
          const id = row.original.id;
          const fieldPath = `entries.${id}.store_id`;
          return (
            <Field
              name={fieldPath}
              render={({ field }) => {
                const rowProjectId =
                  projectId ||
                  (isRoleBasedProject
                    ? (row.original as ReadRoleBasedAssignments).project_id
                    : undefined);

                const selectComponent = !rowProjectId ? (
                  <StoreSelect
                    {...field}
                    className="expand-to-cell"
                    showLink={false}
                    defaultItem={{
                      id: row.original.store_id,
                      name: row.original.store_name,
                    }}
                    queryParams={{ project_dependency: "department" }}
                  />
                ) : (
                  <ProjectStoreSelect
                    {...field}
                    className="expand-to-cell"
                    projectId={rowProjectId}
                    loading="eager"
                    loadingCondition={Boolean(rowProjectId)}
                    showEntityLink={false}
                    defaultItem={{
                      id: row.original.store_id,
                      name: row.original.store_name,
                    }}
                  />
                );
                return (
                  <DirtyAwareCell fieldPath={fieldPath}>
                    {selectComponent}
                  </DirtyAwareCell>
                );
              }}
            />
          );
        },
      },
      status: {
        id: "status",
        cell: ({ row }) =>
          t(`contractorsAssignments.enums.status.${row.original.status}`),
        meta: {
          filter: {
            component: EnumFilter,
            props: {
              options: ASSIGMENT_STATUS,
              translationKey: "contractorsAssignments.enums.status",
            },
          },
        },
      },
    };

    if (isRoleBasedProject) {
      overrides.project_role_name = {
        id: "project_role_name",
        header: t("contractorsAssignments.fields.project_role_name"),
        cell: ({ row }) => {
          const id = row.original.id;
          const fieldPath = `entries.${id}.project_role_id`;
          return (
            <Field
              name={fieldPath}
              render={({ field }) => (
                <DirtyAwareCell fieldPath={fieldPath}>
                  <ProjectRoleSelect
                    {...field}
                    className="expand-to-cell"
                    loading="eager"
                    projectId={
                      (row.original as ReadRoleBasedAssignments).project_id
                    }
                    loadingCondition={Boolean(
                      (row.original as ReadRoleBasedAssignments).project_id,
                    )}
                    defaultItem={{
                      id: (row.original as ReadRoleBasedAssignments)
                        .project_role_id,
                      name: (row.original as ReadRoleBasedAssignments)
                        .project_role_name,
                    }}
                  />
                </DirtyAwareCell>
              )}
            />
          );
        },
      };

      overrides.project_name = {
        id: "project_name",
        cell: ({ row }) => {
          const rowData = row.original as ReadRoleBasedAssignmentsWithRef;

          return (
            <NameWithSuffix
              name={rowData.project_name}
              suffix={rowData.project_ref_number}
            />
          );
        },
      };
    } else {
      overrides.payroll_department_name = {
        id: "payroll_department_name",
        cell: ({ row }) => {
          const id = row.original.id;
          const fieldPath = `entries.${id}.payroll_department_id`;
          return (
            <Field
              name={fieldPath}
              render={({ field }) => (
                <DirtyAwareCell fieldPath={fieldPath}>
                  <PayrollDepartmentSelect
                    {...field}
                    className="expand-to-cell"
                    loading="eager"
                    showEntityLink={false}
                    defaultItem={{
                      id: (row.original as ReadDepartmentBasedAssignments)
                        .payroll_department_id,
                      name: (row.original as ReadDepartmentBasedAssignments)
                        .payroll_department_name,
                    }}
                  />
                </DirtyAwareCell>
              )}
            />
          );
        },
      };
    }

    overrides.actions = {
      id: "actions",
      header: t("contractorsAssignments.removeRow"),
      cell: ({ row }) => (
        <ContractorsAssignmentsRemoveButton
          assignmentId={row.original.id}
          className="expand-to-cell justify-start"
        />
      ),
      enableColumnFilter: false,
      enableSorting: false,
    };

    return overrides;
  }, [projectId, isRoleBasedProject, t]);

  return columnOverrides;
};
